import { useEffect, useState } from 'react';
import {
  Microscope,
  ListChecks,
  Gavel,
  Clock,
  Network,
  EyeOff,
  RefreshCw,
  FileText,
  X,
  Check,
  AlertTriangle,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/cn';
import {
  fmtDuration,
  jobElapsedMs,
  jobEtaRemainingMs,
  KIND_LABEL,
  KIND_VIEW,
  type Job,
  useJobs,
} from '@/shared/jobs';

const KIND_ICON = {
  summary: FileText,
  findings: ListChecks,
  violation: Gavel,
  timeline: Clock,
  entity: Network,
  inconsistency: AlertTriangle,
  pii_llm: EyeOff,
  reindex: RefreshCw,
} as const;

export default function JobItem({ job, compact = false }: { job: Job; compact?: boolean }) {
  const navigate = useNavigate();
  const { cancel, retry } = useJobs();
  const Icon = KIND_ICON[job.kind] ?? Microscope;
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);

  // Tick once a second while RUNNING/PENDING so elapsed/ETA stay live.
  useEffect(() => {
    if (job.status !== 'RUNNING' && job.status !== 'PENDING') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [job.status]);
  // `now` is read implicitly via jobElapsedMs (Date.now()), but we need to
  // re-render — touching the value via a noop keeps the lint linter quiet.
  void now;

  const elapsed = jobElapsedMs(job);
  const etaRem = jobEtaRemainingMs(job);

  const isRunning = job.status === 'RUNNING' || job.status === 'PENDING';
  const isDone = job.status === 'DONE';
  const isFailed = job.status === 'FAILED';
  const isCancelled = job.status === 'CANCELLED';

  const statusColor = isRunning
    ? 'text-brass'
    : isDone
    ? 'text-olive'
    : isFailed
    ? 'text-crimson'
    : 'text-ink-3';

  const StatusIcon = isRunning
    ? Loader2
    : isDone
    ? Check
    : isFailed
    ? AlertTriangle
    : X;

  const bapRef = job.doc_id ? job.doc_id.slice(0, 8) : '—';
  const title = `${KIND_LABEL[job.kind]} · BAP ${bapRef}`;
  const stageLabel = (job.stage || (isRunning ? 'queued' : job.status.toLowerCase()))
    .replace(/_/g, ' ')
    .toUpperCase();

  const navigateToResult = () => {
    if (job.doc_id) {
      navigate(`/berkas/${job.doc_id}?view=${KIND_VIEW[job.kind]}&job=${job.id}`);
    }
  };

  const onCancel = async () => {
    if (!window.confirm(`Batalkan job ${KIND_LABEL[job.kind]}?`)) return;
    setBusy(true);
    try {
      await cancel(job.id);
    } finally {
      setBusy(false);
    }
  };

  const onRetry = async () => {
    setBusy(true);
    try {
      await retry(job.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-b border-ink-line/40 last:border-b-0 px-3 py-2.5 hover:bg-paper-2/40 transition">
      <div className="flex items-start gap-2.5">
        <div className={cn('grid place-items-center w-8 h-8 border border-ink-line bg-paper shrink-0', statusColor)}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <button
            onClick={navigateToResult}
            disabled={!job.doc_id}
            className="block w-full text-left"
          >
            <div className="font-display font-semibold text-[13px] leading-tight truncate hover:text-brass transition">
              {title}
            </div>
          </button>
          <div className={cn('flex items-center gap-2 mt-1 font-mono text-[10px] tracking-wider', statusColor)}>
            <StatusIcon className={cn('w-3 h-3', isRunning && 'animate-spin')} />
            <span className="uppercase truncate">{stageLabel}</span>
          </div>
          {/* Progress bar (or indeterminate) */}
          {isRunning && (
            <div className="mt-2 h-1 bg-ink-line/40 relative overflow-hidden">
              {job.progress != null ? (
                <div
                  className="h-full bg-brass transition-all duration-500"
                  style={{ width: `${Math.max(2, Math.min(100, job.progress))}%` }}
                />
              ) : (
                <div className="absolute inset-y-0 left-0 w-1/3 bg-brass animate-[indeterminate_1.4s_ease-in-out_infinite]" />
              )}
            </div>
          )}
          {/* Failure detail */}
          {isFailed && job.error && !compact && (
            <p className="mt-1.5 font-mono text-[10px] text-crimson leading-snug line-clamp-2">
              {job.error}
            </p>
          )}
          {/* Timing footer */}
          <div className="mt-1.5 flex items-center justify-between font-mono text-[10px] text-ink-3">
            <span>
              {isDone || isCancelled
                ? `selesai · ${fmtDuration(elapsed)}`
                : isFailed
                ? `gagal · ${fmtDuration(elapsed)}`
                : `${fmtDuration(elapsed)}${etaRem != null ? ` · ETA ~${fmtDuration(etaRem)}` : ''}`}
            </span>
          </div>
        </div>
        {/* Action button (cancel / retry) */}
        <div className="shrink-0 flex flex-col gap-1">
          {isRunning && (
            <button
              onClick={onCancel}
              disabled={busy}
              title="Batalkan"
              className="grid place-items-center w-6 h-6 text-ink-3 hover:text-crimson hover:bg-paper-2 transition disabled:opacity-40"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          {isFailed && (
            <button
              onClick={onRetry}
              disabled={busy}
              title="Coba lagi"
              className="grid place-items-center w-6 h-6 text-ink-3 hover:text-brass hover:bg-paper-2 transition disabled:opacity-40"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
